/**
 * Frontend types - must match backend types
 */

export type Phase = 'phase1' | 'phase2' | 'phase3' | 'phase4';

export type AnswerValue = 'yes' | 'no' | 'add-to-cart';

export interface TreeNode {
    id: string;
    question: string;
    recommendation?: string;
    detailedQuestion?: string;
    example?: string;
    /** When true, this question is marked as "Enabling" in the control panel */
    enabling?: boolean;
    children: TreeNode[];
}

export interface AnswerRecord {
    questionId: string;
    value: AnswerValue;
    timestamp: string;
}

export interface Session {
    id: string;
    phase: Phase;
    area: string;
    answers: AnswerRecord[];
    cartIds: string[];
    createdAt: string;
    updatedAt: string;
}

// Report item from GET /api/session/:id/report
export interface ReportItem {
    questionId: string;
    question: string;
    value: AnswerValue;
    timestamp: string;
    /** True when this indicator is marked enabling in the control panel */
    enabling?: boolean;
}

// Shopping list item from GET /api/session/:id/cart
export interface ShoppingListItem {
    questionId: string;
    question: string;
    recommendation?: string;
    detailedQuestion?: string;
    example?: string;
    stakeholderSuggestions: string[];
}

// Traversal state for navigating the tree
export interface TraversalState {
    currentNode: TreeNode | null;
    path: number[];  // indices at each level to track position
    isComplete: boolean;
}

// App view states
export type AppView = 'landing' | 'names' | 'entry' | 'intro' | 'assessment' | 'results' | 'visualizer' | 'feedback' | 'feedback-admin';

// Lifecycle stage labels
export const PHASE_LABELS: Record<Phase, string> = {
    phase1: 'Exploratory',
    phase2: 'Prototyping/Testing',
    phase3: 'Post-deployment Monitoring',
    phase4: 'Scaling'
};

// Short explanation shown next to each phase in the selector
export const PHASE_EXPLANATIONS: Record<Phase, string> = {
    phase1: "We're just thinking",
    phase2: "We're testing",
    phase3: "We have it running",
    phase4: "We want to scale"
};

// Placeholder areas for examples (can be extended later), ordered by likeliness of use
export const EXAMPLE_AREAS = [
    'Education',
    'Healthcare',
    'Social care',
    'Workplace training',
    'Infrastructure',
    'Heritage',
    'Public Media & Arts',
    'Justice & Forensics',
    'Defense'
];

// Area descriptions for context
export const AREA_DESCRIPTIONS: Record<string, string> = {
    'Education': 'Immersive learning environments and academic inquiry.',
    'Healthcare': 'Clinical skills training and therapeutic patient interventions.',
    'Social care': 'Support for daily living, behavior, and reintegration.',
    'Workplace training': 'Trainings in healthcare and other areas.',
    'Infrastructure': 'Digital twins for planning and maintaining physical systems.',
    'Heritage': 'Digital preservation and promotion of cultural assets.',
    'Public Media & Arts': 'Accessible storytelling and performance for the public.',
    'Justice & Forensics': 'Evidence visualization for legal proceedings.',
    'Defense': 'Tactical simulations for high-stakes operations and crisis readiness.'
};